/**
 *
 * This class provides a consistent, reliable way to store data locally in the user's browser. It uses localStorage if
 * available, or cookies as a fallback if localStorage isn't available (for example, in an incognito/private window).
 * Either way, it provides a consistent interface for working with stored data based on the localStorage interface, plus
 * some additions and enhancements, like automatically serializing and unserializing JSON data.
 *
 **/
export default class WindCreekStorage {
  static getItem(key, raw = false) {
    let value;

    if (WindCreekStorage.isLocalStorageAvailable()) {
      value = localStorage.getItem(key);
    } else {
      try {
        value = document.cookie
          .split(';')
          .find(item => item.trim().startsWith(key))
          .split('=')[1];
      } catch {
        return null;
      }
    }

    if (!raw) {
      value = decodeURIComponent(value);
      value = JSON.parse(value);
    }

    return value;
  }

  static setItem(key, value, raw = false) {
    if (!raw) {
      value = JSON.stringify(value);
    }

    if (WindCreekStorage.isLocalStorageAvailable()) {
      localStorage.setItem(key, value);
    } else {
      document.cookie = key + '=' + encodeURIComponent(value) + '; path=/; max-age=31536000;';
    }
  }

  static exists(key) {
    if (WindCreekStorage.isLocalStorageAvailable()) {
      return localStorage.getItem(key) !== null;
    } else {
      return document.cookie.split(';').some((item) => item.trim().startsWith(key + '='));
    }
  }

  static removeItem(key) {
    if (WindCreekStorage.isLocalStorageAvailable()) {
      localStorage.removeItem(key);
    } else {
      document.cookie = key + '=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;';
    }
  }

  // See https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#Feature-detecting_localStorage
  static isLocalStorageAvailable() {
    let storage;
    try {
      storage = window.localStorage;
      const x = "__storage_test__";
      storage.setItem(x, x);
      storage.removeItem(x);
      return true;
    } catch (e) {
      return (
        e instanceof DOMException &&
        // everything except Firefox
        (e.code === 22 ||
          // Firefox
          e.code === 1014 ||
          // test name field too, because code might not be present
          // everything except Firefox
          e.name === "QuotaExceededError" ||
          // Firefox
          e.name === "NS_ERROR_DOM_QUOTA_REACHED") &&
        // acknowledge QuotaExceededError only if there's something already stored
        storage &&
        storage.length !== 0
      );
    }
  }
}
